import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { AxiosError } from 'axios'
import { PiBuildings, PiCircleNotch, PiListChecks } from 'react-icons/pi'
import { type RightPanelState } from './RightPanel'
import { applyRecommendation } from '../../services/recommendations_api'
import { notifySuccess } from '../../services/toast'
import { cn } from '../UI/cn'
import { TabPanel } from '../FbUI/PillTabs'
import {
  SegmentedControl,
  Button,
  Badge,
  Skeleton,
  Spinner,
  Alert,
  AlertIndicator,
  AlertContent,
  AlertTitle,
  AlertDescription,
  CompletedActionsPanel,
  type SegmentedControlOption,
} from '@firstbite/ui'
import { buildCompletedItems } from './completedActionItems'
import {
  getLatestActionSummary,
  getRecommendationRefreshStatus,
  ActionSummaryData,
} from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import {
  ApplyRecommendationPanel,
  EnrichedActionCard,
  EnrichedActionCardSkeleton,
} from './EnrichedActionCard'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { URGENCY_ICONS } from './constants'
import { resolveAgentIcon } from './agentIcons'
import useApiService from '../../services/api'
import { useUserOptions } from 'services/users'
import { useRecommendationsPolling } from './useRecommendationsPolling'
import { useActionClickManager } from './useActionClickManager'
import { useAggregationRefresh } from '../DataTable/hooks/useAggregationRefresh'
import {
  buildActionViewOptions,
  groupActionsByAgent,
  groupActionsByCompany,
  partitionActionsByStatus,
  resolveActionViewMode,
  sortByUrgencyScore,
} from './recommendationVisibility'

const REC_TABS_ID = 'recommendations-view'

type ViewMode = 'priority' | 'signal' | 'company'

/** Urgency bands, in priority order — the section headers + order for the
 *  priority (default) grouping of active actions. */
const URGENCY_BANDS: Array<{
  key: EnrichedAction['urgency']
  label: string
}> = [
  { key: 'do_now', label: 'Hot' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
]

interface RecommendationsCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
  /** Content rendered on the left of the fixed header row (e.g. "Action Center" title). */
  headerSlot?: React.ReactNode
  /** Content rendered on the far right of the fixed header row (e.g. "Manage AI Agents" button). */
  headerTrailingSlot?: React.ReactNode
  /** Controlled right-panel state. Tagged union so the card can ask
   *  the parent to open different kinds of panels (recently-completed,
   *  apply-rec, etc.) without lifting per-kind state. Falls back to
   *  internal state when omitted so the card still works standalone. */
  activePanel?: RightPanelState
  onActivePanelChange?: (next: RightPanelState) => void
}

/**
 * Displays AI-generated daily recommendations for the current user.
 *
 * Shows prioritized action items grouped by urgency (default), signal type, or company.
 * All three views share the same flat design language: inline group headers + card lists.
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const RecommendationsCard: React.FC<RecommendationsCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
  headerSlot,
  headerTrailingSlot,
  activePanel: activePanelProp,
  onActivePanelChange,
}) => {
  const queryClient = useQueryClient()
  const [rawViewMode, setViewMode] = useState<ViewMode>('priority')
  // Controlled when the parent supplies ``activePanel`` + setter;
  // otherwise falls back to internal state so the component still
  // works standalone (e.g. component-catalog previews, tests).
  const [internalActivePanel, setInternalActivePanel] =
    useState<RightPanelState>({ kind: 'closed' })
  const isControlled =
    activePanelProp !== undefined && onActivePanelChange !== undefined
  const activePanel = isControlled ? activePanelProp : internalActivePanel
  const setActivePanel = useCallback(
    (next: RightPanelState) => {
      if (isControlled && onActivePanelChange) {
        onActivePanelChange(next)
      } else {
        setInternalActivePanel(next)
      }
    },
    [isControlled, onActivePanelChange]
  )
  // Convenience accessor — the recently-completed button toggles
  // between this kind and ``closed``.
  const showResolved = activePanel.kind === 'recently-completed'
  const setShowResolved = useCallback(
    (open: boolean) => {
      setActivePanel(open ? { kind: 'recently-completed' } : { kind: 'closed' })
    },
    [setActivePanel]
  )

  const api = useApiService()
  const { data: companyData } = api.useGetCompanyDetails()
  // Prefetch the data the embedded compose + note editors block on inside
  // their useEffect. Without this, clicking Apply right after a reload
  // mounts the modal before these queries resolve, leaving Quill
  // uninitialized (empty body, no toolbar) AND leaving the compose
  // form's from_id at 0 — which fails zod validation silently and the
  // submit handler never fires. We gate Apply on both queries below so
  // the dialog can't open early.
  const { isLoading: isUserOptionsLoading } = useUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })
  const { data: integrationOptions, isLoading: isIntegrationOptionsLoading } =
    api.emailIntegration.useGetEmailIntegrationOptions()
  const isEditorDataReady =
    !isUserOptionsLoading && !isIntegrationOptionsLoading
  // Whether the rec owner has at least one connected email integration
  // capable of sending. ``can_send`` is the backend-derived "matching
  // email + synced + not expired" flag. Forwarded to the apply panel
  // so a ``send_email`` prepared action is force-disabled when there's
  // no integration.
  const hasEmailIntegration = (integrationOptions ?? []).some(
    (opt) => opt.original?.can_send
  )
  // Recommendation agents come from the backend AGENT_REGISTRY
  // (server/recommendations/pipeline/registry.py) via the company-details
  // payload. The frontend never hardcodes which signal_types belong to
  // which agent — that decision lives backend-side so the two cannot drift.
  const recommendationAgents = useMemo(
    () => companyData?.recommendation_agents ?? [],
    [companyData?.recommendation_agents]
  )
  const signalTypeToAgentId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const agent of recommendationAgents) {
      for (const signalType of agent.signal_types) {
        map[signalType] = agent.id
      }
    }
    return map
  }, [recommendationAgents])
  // Agent sections used by the "Type" view. Filter out agents whose every
  // signal type is disabled — a fully-off agent has no actions to show
  // and rendering an empty section just adds visual noise.
  const filteredAgentSections = useMemo(
    () =>
      recommendationAgents
        .filter((a) => a.enabled)
        .map((a) => ({
          key: a.id,
          title: a.label,
          Icon: resolveAgentIcon(a.icon),
        })),
    [recommendationAgents]
  )
  // "Type" groups by AI agent, so it only applies with enabled agents — the
  // option is hidden and the mode falls back to Priority otherwise (see
  // resolveActionViewMode / buildActionViewOptions for the tested logic).
  const hasEnabledAgents = filteredAgentSections.length > 0
  const viewMode = resolveActionViewMode(rawViewMode, hasEnabledAgents)
  const viewOptions: SegmentedControlOption[] =
    buildActionViewOptions(hasEnabledAgents)

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['recommendations', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch recommendations'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Polling for GENERATING status
  useRecommendationsPolling({
    summary,
    refetch: summaryQuery.refetch,
    userId,
  })

  // Poll for recommendation refresh status — when CRM entities change, the
  // backend queues an LLM re-evaluation. This refetches the summary on completion.
  const { refreshStatus } = useAggregationRefresh({
    queryKey: ['recommendations-refresh-status'],
    queryFn: getRecommendationRefreshStatus,
    getIsRefreshing: (data) => data.is_refreshing,
    onRefetch: useCallback(() => summaryQuery.refetch(), [summaryQuery]),
    refetchInterval: 5000,
    enabled: userId === undefined,
  })

  // Set of fingerprints currently being re-evaluated — only those cards
  // get disabled/skeleton treatment, not the entire list.
  const refreshingFingerprints = useMemo(
    () => new Set(refreshStatus?.refreshing_fingerprints ?? []),
    [refreshStatus?.refreshing_fingerprints]
  )

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  useEffect(() => {
    setShowResolved(false)
  }, [summary?.id, userId])

  // Action click management
  const {
    handleActionClick,
    handleStatusClick,
    getStatusForAction,
    getAutoCompletedForAction,
  } = useActionClickManager({ summary })

  // The modal POSTs to the atomic apply endpoint itself; this callback
  // just refreshes the data once the backend has marked the rec resolved
  // and recorded the click. If the apply failed, the modal stays open
  // with an error toast and we never get here.
  const handleAppliedRecommendation = async (action: EnrichedAction) => {
    queryClient.removeQueries({
      queryKey: ['deal', String(action.deal_id)],
    })
    await summaryQuery.refetch()
  }

  // Non-action resolution path: user unchecked every prepared action
  // and clicked Apply, so we just mark the rec done client-side
  // (no server call to apply needed).
  const handleNoOpResolution = async (action: EnrichedAction) => {
    await handleStatusClick(action, 'done', false)
    await summaryQuery.refetch()
  }

  const allActions = useMemo(
    () => summary?.summary_data?.enriched_actions ?? [],
    [summary]
  )
  const { activeActions, resolvedActions } = useMemo(
    () => partitionActionsByStatus(allActions, getStatusForAction),
    [allActions, getStatusForAction]
  )

  const actionsByUrgency = useMemo(
    () => sortByUrgencyScore(activeActions),
    [activeActions]
  )
  const actionsByAgent = useMemo(
    () => groupActionsByAgent(activeActions, signalTypeToAgentId),
    [activeActions, signalTypeToAgentId]
  )
  // Keyed agent map for resolved actions — still drives `hasSignalData` (which
  // tab/stats show). The completed panel itself is day-grouped by the fb-ui
  // CompletedActionsPanel block via `completedItems` below.
  const resolvedActionsByAgent = useMemo(
    () => groupActionsByAgent(resolvedActions, signalTypeToAgentId),
    [resolvedActions, signalTypeToAgentId]
  )
  const actionsByCompany = useMemo(
    () => groupActionsByCompany(activeActions),
    [activeActions]
  )

  // Resolved actions mapped into the fb-ui CompletedActionsPanel item model —
  // day-grouped, newest-first, with applied steps / auto-resolution reasons and
  // linked records. The panel owns its own search + day sectioning, so we pass
  // the full resolved set (unfiltered). Keyed on the resolved set + the status
  // selectors so the badges/steps stay in sync with in-session resolutions.
  const completedItems = useMemo(
    () =>
      buildCompletedItems(resolvedActions, {
        getStatus: getStatusForAction,
        getAutoCompleted: getAutoCompletedForAction,
      }),
    [resolvedActions, getStatusForAction, getAutoCompletedForAction]
  )

  /**
   * Wrap a status-specific body (loading skeleton, error card, no-summary
   * card, FAILED card, GENERATING card) in the same shell the data-rich
   * path uses. The shell keeps ``headerSlot`` mounted so the user picker,
   * super-user regen button, and collapse control stay reachable in every
   * status — without this, hitting an error would strand a superuser on
   * a single failing summary with no way to switch to a different user
   * or kick a regen. The "Manage AI Agents" footer is anchored by the
   * parent layout (sibling, not child), so it survives independently.
   */
  const renderStatusShell = (body: React.ReactNode): React.ReactNode => (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      <div className="flex-shrink-0 pb-4 border-b border-fbui-gray-200 bg-fbui-base-white -mx-6 px-6">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">{headerSlot}</div>
          {headerTrailingSlot}
        </div>
      </div>
      <div className="flex-1 overflow-auto pt-4 pb-6">{body}</div>
    </div>
  )

  if (summaryQuery.isLoading) {
    return renderStatusShell(
      <div className="flex flex-col gap-4">
        {/* Stats header placeholder */}
        <div className="flex items-center gap-4">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
        </div>
        {/* Card placeholders — same component used for the per-card
         *  applying state so the visuals are consistent. */}
        {[0, 1, 2, 3].map((i) => (
          <EnrichedActionCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return renderStatusShell(
      <Alert status="error">
        <AlertIndicator />
        <AlertContent>
          <AlertTitle>Error loading recommendations</AlertTitle>
          <AlertDescription>{queryError.message}</AlertDescription>
        </AlertContent>
      </Alert>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return renderStatusShell(
      <div className="rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-6">
        <div className="text-center">
          <PiListChecks
            className="mx-auto h-12 w-12 text-fbui-gray-400"
            aria-hidden
          />
          <h3 className="mt-2 text-base font-medium text-fbui-gray-900">
            No recommendations available
          </h3>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return renderStatusShell(
      <Alert status="warning">
        <AlertIndicator />
        <AlertContent>
          <AlertTitle>Summary generation failed</AlertTitle>
          {summary.error_message && (
            <AlertDescription>{summary.error_message}</AlertDescription>
          )}
        </AlertContent>
      </Alert>
    )
  }

  if (summary.status === 'GENERATING') {
    return renderStatusShell(
      <div className="rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-6">
        <div className="flex items-center gap-2 text-fbui-gray-700">
          <Spinner size="sm" className="text-fbui-gray-500" />
          <span className="font-medium">
            Generating your recommendations...
          </span>
        </div>
        <p className="mt-2 text-base text-fbui-gray-500">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions = allActions.length > 0
  const hasActiveActions = activeActions.length > 0
  const hasResolvedActions = resolvedActions.length > 0
  const hasSignalData =
    Object.keys(actionsByAgent).length > 0 ||
    (showResolved && Object.keys(resolvedActionsByAgent).length > 0)

  /** Shared empty state shown when no active actions exist in any view. */
  const emptyState = (
    <div className="rounded-xl border border-dashed border-fbui-gray-300 bg-fbui-base-white px-4 py-5 text-center">
      <p className="text-base font-medium text-fbui-gray-900">
        No active recommendations
      </p>
      <p className="mt-1 text-base text-fbui-gray-500">
        {hasResolvedActions
          ? 'Resolved items are hidden from your active queue.'
          : 'Your pipeline looks healthy.'}
      </p>
    </div>
  )

  /**
   * Render a group header matching the priority-view band style:
   * icon + label + muted count.
   */
  const renderGroupHeader = (Icon: React.ElementType, label: string) => (
    <div className="flex items-center gap-1.5 mb-2 px-0.5">
      <Icon size={16} className="text-fbui-gray-500" />
      <span className="text-sm font-semibold uppercase tracking-wide text-fbui-gray-500">
        {label}
      </span>
    </div>
  )

  /**
   * Render a list of action cards for a group.
   *
   * ``dimWhenResolved`` defaults to true, which is the right setting
   * for the main active-list (already-resolved cards fade visually so
   * the user's eye can skip them). The recently-completed side panel
   * passes ``false`` so its cards render at full opacity.
   */
  const renderCards = (
    actions: EnrichedAction[],
    keyPrefix: string,
    dimWhenResolved: boolean = true
  ) => (
    <div className="space-y-4">
      {actions.map((action, index) => (
        <EnrichedActionCard
          key={`${keyPrefix}-${action.signal_fingerprint ?? `${action.deal_id}-${index}`}`}
          action={action}
          dimWhenResolved={dimWhenResolved}
          onCardClick={() =>
            handleActionClick(action, action.signal_type, action.action_type)
          }
          readOnly={!!userId}
          isRefreshing={
            // Disable Apply/Dismiss while any of:
            //  (a) the per-rec refresh poller flagged this signal (the
            //      LLM is re-evaluating it),
            //  (b) we're refetching the whole summary (e.g. right after
            //      a successful apply, before the new resolution state
            //      lands), or
            //  (c) the user-options query the embedded Quill editors
            //      block on hasn't resolved yet — without this, the
            //      modal can mount before Quill initializes and shows
            //      empty body editors with no toolbar.
            refreshingFingerprints.has(action.signal_fingerprint ?? '') ||
            summaryQuery.isFetching ||
            !isEditorDataReady
          }
          onStatusClick={(status) => handleStatusClick(action, status)}
          actionStatus={getStatusForAction(action)}
          actionAutoCompleted={getAutoCompletedForAction(action)}
          onApplied={() => handleAppliedRecommendation(action)}
          onNoOpResolve={() => handleNoOpResolution(action)}
          onApplyClick={(a) => setActivePanel({ kind: 'apply-rec', action: a })}
          recommendationAgents={recommendationAgents}
        />
      ))}
    </div>
  )

  // No actions in any view → reuse the status shell so the user picker,
  // super-user regen, and collapse stay in the same fixed-position header
  // they occupy in error / loading / generating states. The data-rich
  // path below has its own header-with-stats wrapper since it also needs
  // to layer in SummaryStatsHeader and the view-mode tabs.
  if (!hasActions) {
    return renderStatusShell(
      <div className="rounded-xl border border-dashed border-fbui-gray-300 bg-fbui-base-white p-6 text-center">
        <p className="text-base font-medium text-fbui-gray-900">
          No active recommendations
        </p>
        <p className="mt-1 text-base text-fbui-gray-500">
          Your pipeline looks healthy.
        </p>
      </div>
    )
  }

  // ``hasActions`` is true at this point — the no-actions branch above
  // returned early via ``renderStatusShell``. The wrapper below is the
  // data-rich layout: pinned header (slot + stats + view controls) and
  // a scrollable body of grouped action cards.
  //
  // The grouping switch, completed toggle, and Manage AI Agents appear once
  // there's something actionable to control.
  const showControls = hasSignalData || hasResolvedActions || summary.created
  return (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      {/* Fixed header zone: title row + stats. Outer border-b is the
          canonical separator for every state (with/without stats); the
          previous `h-px` wrapper only fired when summary_data was loaded. */}
      <div className="flex-shrink-0 bg-fbui-base-white -mx-6 px-6 pb-4 flex flex-col gap-4 border-b border-fbui-gray-200">
        {/* Title row — only when a caller supplies a heading. The homepage now
            drops it: the "Action Center" tab label already names this view. */}
        {headerSlot && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">{headerSlot}</div>
          </div>
        )}

        {/* Summary row — stats (left) + the completed toggle (right), matching
            the fb-ui ActionCenter block's arrangement. */}
        {(summary.summary_data || showControls) && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              {summary.summary_data && (
                <SummaryStatsHeader
                  data={summary.summary_data}
                  visibleActions={activeActions}
                  viewMode={viewMode}
                  companiesCount={actionsByCompany.length}
                  recommendationAgents={recommendationAgents}
                />
              )}
            </div>
            {showControls && (
              <div className="flex items-center gap-3 flex-shrink-0">
                {(summaryQuery.isFetching ||
                  refreshingFingerprints.size > 0 ||
                  !isEditorDataReady) && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-fbui-gray-500">
                    <PiCircleNotch size={14} className="animate-spin" />
                    Updating…
                  </span>
                )}
                <CompletedActionsPanel
                  isOpen={showResolved}
                  onOpenChange={setShowResolved}
                  items={completedItems}
                  searchPlaceholder="Search by company, deal, contact, or keyword"
                  emptyState={
                    <p className="mt-8 text-center text-base text-fbui-gray-500">
                      Nothing completed yet.
                    </p>
                  }
                  trigger={
                    <Button
                      variant="primary"
                      appearance="outline"
                      size="md"
                      leftIcon={<PiListChecks />}
                    >
                      Completed
                      <Badge color="gray" size="md" className="ml-1.5">
                        {resolvedActions.length}/
                        {activeActions.length + resolvedActions.length}
                      </Badge>
                    </Button>
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Controls row — grouping (left) + Manage AI Agents (right), the block's
            second toolbar row. */}
        {showControls && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-fbui-gray-500 whitespace-nowrap">
                Group by
              </span>
              <SegmentedControl
                aria-label="Group recommendations by"
                size="md"
                variant="subtle"
                options={viewOptions}
                value={viewMode}
                onValueChange={(v) => setViewMode(v as ViewMode)}
              />
            </div>
            {headerTrailingSlot}
          </div>
        )}
      </div>

      {/* Scrollable action cards */}
      <div className="flex-1 overflow-auto space-y-6 pt-4 pb-6 bg-fbui-gray-50 -mx-6 px-6">
        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="priority"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-8">
              {URGENCY_BANDS.map(({ key, label }) => {
                const UrgencyIcon = URGENCY_ICONS[key]
                const bandActions = actionsByUrgency.filter(
                  (a) => a.urgency === key
                )
                if (bandActions.length === 0) return null
                return (
                  <div key={key}>
                    {renderGroupHeader(UrgencyIcon, label)}
                    {renderCards(bandActions, `priority-${key}`)}
                  </div>
                )
              })}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>

        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="signal"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-4">
              {filteredAgentSections.map(({ key, title, Icon }) => {
                const sectionActions = actionsByAgent[key] ?? []
                if (sectionActions.length === 0) return null
                return (
                  <div key={key}>
                    {renderGroupHeader(Icon, title)}
                    {renderCards(sectionActions, `agent-${key}`)}
                  </div>
                )
              })}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>

        <TabPanel<ViewMode>
          idPrefix={REC_TABS_ID}
          tabKey="company"
          activeKey={viewMode}
          className="space-y-4"
        >
          {hasActiveActions ? (
            <div className="space-y-4">
              {actionsByCompany.map((company) => (
                <div key={company.companyId}>
                  {renderGroupHeader(PiBuildings, company.companyName)}
                  {renderCards(company.actions, `company-${company.companyId}`)}
                </div>
              ))}
            </div>
          ) : (
            emptyState
          )}
        </TabPanel>
      </div>

      {/* Apply / compose panel — a surface-scoped fb-ui Sheet. It portals into
          the homepage content region via the OverlayScopeProvider the page sets
          up, so it dims only the content area and leaves the app nav reachable
          (no bespoke sider host / width animation needed). Mounted only while an
          apply-rec panel is active. */}
      {activePanel.kind === 'apply-rec' && (
        <ApplyRecommendationPanel
          onClose={() => setActivePanel({ kind: 'closed' })}
          action={activePanel.action}
          preparedActions={activePanel.action.prepared_actions ?? []}
          onApply={async (formData, count) => {
            const action = activePanel.action
            if (action.id == null) return
            // Close the panel immediately and fire the apply in the background —
            // same UX the modal had. The card's refresh poller will reflect the
            // new state once the backend resolves.
            setActivePanel({ kind: 'closed' })
            try {
              await applyRecommendation(action.id, formData)
              notifySuccess(
                `${count} ${count === 1 ? 'action' : 'actions'} applied`
              )
              handleAppliedRecommendation(action)
            } catch {
              // baseAPICall already surfaced an error toast.
            }
          }}
          onNoOpResolve={() => {
            handleNoOpResolution(activePanel.action)
          }}
          mutuallyExclusiveActions={
            summary?.summary_data?.mutually_exclusive_actions ?? []
          }
          hasEmailIntegration={hasEmailIntegration}
          recommendationAgents={recommendationAgents}
        />
      )}
    </div>
  )
}
